import React, { useCallback, useEffect, useState } from "react";
import { Eye, Pencil, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../Input/Input";
import Button from "../../Button/Button";
import APITable from "../../Tables/APITable";
import Pagination from "../../Pagination/Pagination";
import Modal from "../../Modal/Modal";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import Notification from "../../Notification/Notification";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import { TableSkeleton } from "../../Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet, apiPatch, apiPut } from "../../../apiService";

/* ===================== UTILS ===================== */
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./LosCredentials.module.scss";

const ipRegex =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const maskValue = (value, start = 3, end = 3) => {
  if (!value) return "--";
  if (value.length <= start + end) return value;
  return `${value.slice(0, start)}${"*".repeat(value.length - (start + end))}${value.slice(-end)}`;
};

const formatDate = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ISO -> YYYY-MM-DD for <input type="date">
const toDateInput = (iso) => (iso ? String(iso).split("T")[0] : "");

const LosCredentialsTable = ({ isAdmin = false }) => {
  // Action-level permissions apply to the admin "api-keys" module only; the
  // client-facing page keeps its full controls.
  const canDo = (action) => !isAdmin || can("api-keys", action);

  /* =========================
     STATE
  ========================= */
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [orgOptions, setOrgOptions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [viewModal, setViewModal] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editIpInput, setEditIpInput] = useState("");
  const [statusModal, setStatusModal] = useState(null);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     FETCH LIST
  ========================= */
  const fetchCredentials = useCallback(
    async (pageNo = currentPage, limit = rowsPerPage) => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ page: pageNo, limit });
        if (search) params.set("search", search);
        if (statusFilter) params.set("status", statusFilter);
        if (orgFilter) params.set("orgId", orgFilter);

        const res = await apiGet(
          "catalog",
          `/los/credentials?${params.toString()}`,
        );

        if (res?.status === "success") {
          const formatted = (res.data?.credentials || []).map((c) => ({
            id: c.id,
            companyName: c.companyName || "--",
            name: c.keyName,
            client: maskValue(c.clientKey),
            clientRaw: c.clientKey,
            products: c.products || [],
            ipList: c.ipWhitelist || [],
            ipCount: c.ipWhitelist?.length || 0,
            status: c.status,
            active: c.status === "Active",
            expiresAt: c.expiresAt,
            isExpired: c.isExpired,
          }));
          setCreds(formatted);
          if (res.data?.pagination) setPagination(res.data.pagination);
        } else {
          showNotification("Failed to fetch LOS credentials", "error");
        }
      } catch {
        showNotification("Something went wrong", "error");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, rowsPerPage, search, statusFilter, orgFilter],
  );

  useEffect(() => {
    fetchCredentials(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, statusFilter, orgFilter, fetchCredentials]);

  // Organization filter options (internal users only).
  const fetchOrganizations = useCallback(async () => {
    try {
      const res = await apiGet("user", "/organization/dropdown");
      if (res?.status === "success" && Array.isArray(res.data)) {
        setOrgOptions(
          res.data.map((o) => ({ label: o.companyName, value: String(o.id) })),
        );
      }
    } catch (err) {
      console.error("Failed to load organizations", err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchOrganizations();
  }, [isAdmin, fetchOrganizations]);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  /* =========================
     VIEW
  ========================= */
  const openView = async (id) => {
    try {
      setViewModal({ loading: true });
      setViewLoading(true);
      const res = await apiGet("catalog", `/los/credentials/${id}`);
      if (res?.status === "success") {
        const c = res.data.credential;
        setViewModal({
          name: c.keyName,
          clientRaw: c.clientKey,
          clientSecret: c.clientSecret,
          products: c.products || [],
          ipList: c.ipWhitelist || [],
          status: c.status,
          expiresAt: c.expiresAt,
        });
      } else {
        showNotification("Failed to fetch credential", "error");
        setViewModal(null);
      }
    } catch {
      showNotification("Something went wrong", "error");
      setViewModal(null);
    } finally {
      setViewLoading(false);
    }
  };

  /* =========================
     EDIT (status + expiry + ips)
  ========================= */
  const openEdit = (row) => {
    setEditIpInput("");
    setEditModal({
      id: row.id,
      status: row.status,
      expiry: toDateInput(row.expiresAt),
      ipList: [...row.ipList],
      _orig: {
        status: row.status,
        expiry: toDateInput(row.expiresAt),
        ipList: [...row.ipList],
      },
    });
  };

  const addEditIp = () => {
    const trimmed = editIpInput.trim();
    if (!trimmed || !ipRegex.test(trimmed)) return;
    setEditModal((prev) =>
      prev.ipList.includes(trimmed)
        ? prev
        : { ...prev, ipList: [...prev.ipList, trimmed] },
    );
    setEditIpInput("");
  };

  const saveEdit = async () => {
    const m = editModal;
    if (!m) return;
    setEditSaving(true);

    try {
      const tasks = [];

      // Status changed → PATCH /status
      if (m.status !== m._orig.status) {
        tasks.push(
          apiPatch("catalog", `/los/credentials/${m.id}/status`, {
            status: m.status,
          }),
        );
      }

      // Expiry changed → PATCH /expiry
      if (m.expiry !== m._orig.expiry) {
        tasks.push(
          apiPatch("catalog", `/los/credentials/${m.id}/expiry`, {
            expiresAt: m.expiry ? `${m.expiry}T00:00:00Z` : null,
          }),
        );
      }

      // IPs changed → PUT /ips
      if (JSON.stringify(m.ipList) !== JSON.stringify(m._orig.ipList)) {
        tasks.push(
          apiPut("catalog", `/los/credentials/${m.id}/ips`, {
            ipWhitelist: m.ipList,
          }),
        );
      }

      if (tasks.length === 0) {
        setEditModal(null);
        return;
      }

      await Promise.all(tasks);
      showNotification("Credential updated successfully");
      setEditModal(null);
      fetchCredentials();
    } catch {
      showNotification("Failed to update credential", "error");
    } finally {
      setEditSaving(false);
    }
  };

  /* =========================
     QUICK STATUS TOGGLE
  ========================= */
  const confirmToggle = async () => {
    if (!statusModal) return;
    const row = statusModal;
    const newStatus = row.active ? "Inactive" : "Active";
    const prev = [...creds];

    setCreds((list) =>
      list.map((c) =>
        c.id === row.id ? { ...c, active: !c.active, status: newStatus } : c,
      ),
    );

    try {
      const res = await apiPatch(
        "catalog",
        `/los/credentials/${row.id}/status`,
        { status: newStatus },
      );
      if (res?.status === "success") {
        showNotification("Status updated successfully");
      } else {
        throw new Error("failed");
      }
    } catch {
      setCreds(prev);
      showNotification("Status update failed", "error");
    } finally {
      setStatusModal(null);
    }
  };

  /* =========================
     TABLE CONFIG
  ========================= */
  const tableData = creds.map((item) => ({
    ...item,
    onView: () => openView(item.id),
    onEdit: () => openEdit(item),
    onToggle: () => setStatusModal(item),
  }));

  const columns = [
    ...(isAdmin ? [{ label: "Company Name", key: "companyName" }] : []),
    { label: "Key Name", key: "name" },
    { label: "Client ID", key: "client" },
    { label: "Products", key: "products" },
    { label: "IPs", key: "ipAddress" },
    { label: "Expiry", key: "expiry" },
    { label: "Status", key: "status" },
    { label: "Manage", key: "action" },
  ];

  /* =========================
     JSX
  ========================= */
  return (
    <>
      {notification && (
        <div className={styles["los-cred__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["los-cred__card"]}>
        {/* FILTERS */}
        <div className={styles["los-cred__filter-bar"]}>
          <div className={styles["los-cred__search-group"]}>
            <Input
              type="search"
              placeholder="Search Key Name or Client ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" onClick={handleSearch} />
          </div>

          <div className={styles["los-cred__filter-controls"]}>
            {isAdmin && (
              <div className={styles["los-cred__filter-select"]}>
                <Input
                  type="select"
                  placeholder="All Organizations"
                  value={orgFilter}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setOrgFilter(e.target.value);
                  }}
                  options={[
                    { label: "All Organizations", value: "" },
                    ...orgOptions,
                  ]}
                />
              </div>
            )}

            <div className={styles["los-cred__filter-select"]}>
              <Input
                type="select"
                placeholder="All Status"
                value={statusFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setStatusFilter(e.target.value);
                }}
                options={[
                  { label: "All Status", value: "" },
                  { label: "Active", value: "Active" },
                  { label: "Inactive", value: "Inactive" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className={styles["los-cred__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={columns.length} rows={4} />
          ) : (
            <APITable
              columns={columns}
              data={tableData}
              renderCell={(row, key) => {
                if (key === "products") {
                  if (!row.products.length) return "--";
                  return (
                    <div className={styles["los-cred__product-chips"]}>
                      {row.products.map((p) => (
                        <span
                          key={p.apiMasterId || p.id || p.name}
                          className={styles["los-cred__product-chip"]}
                        >
                          {p.name}
                        </span>
                      ))}
                    </div>
                  );
                }

                if (key === "ipAddress") {
                  if (!canDo("update-los")) {
                    return <span>{row.ipCount} IPs</span>;
                  }
                  return (
                    <span
                      className={styles["los-cred__ip-link"]}
                      onClick={row.onEdit}
                      title="Manage IPs"
                    >
                      {row.ipCount} IPs
                    </span>
                  );
                }

                if (key === "expiry") {
                  return (
                    <span>
                      {formatDate(row.expiresAt)}
                      {row.isExpired && (
                        <span
                          className={`${styles["los-cred__badge"]} ${styles["los-cred__badge--expired"]}`}
                        >
                          Expired
                        </span>
                      )}
                    </span>
                  );
                }

                if (key === "status") {
                  const cls = row.active
                    ? styles["los-cred__badge--active"]
                    : styles["los-cred__badge--inactive"];
                  return (
                    <span className={`${styles["los-cred__badge"]} ${cls}`}>
                      {row.status}
                    </span>
                  );
                }

                if (key === "action") {
                  return (
                    <div className={styles["los-cred__action-cell"]}>
                      <div
                        className={styles["los-cred__icon-btn"]}
                        onClick={row.onView}
                        title="View Details"
                      >
                        <Eye size={18} strokeWidth={2.5} />
                      </div>
                      {canDo("update-los") && (
                        <div
                          className={styles["los-cred__icon-btn"]}
                          onClick={row.onEdit}
                          title="Edit Credential"
                        >
                          <Pencil size={18} strokeWidth={2.5} />
                        </div>
                      )}
                      {canDo("active-inactive-los-creds") && (
                        <div title="Toggle Status">
                          <ToggleSwitch
                            isOn={row.active}
                            onToggle={row.onToggle}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* STATUS CONFIRM */}
      <ConfirmModal
        open={!!statusModal}
        title="Are you sure you want to change the status?"
        onClose={() => setStatusModal(null)}
        onConfirm={confirmToggle}
      />

      {/* VIEW MODAL */}
      {viewModal && (
        <Modal
          onClose={() => setViewModal(null)}
          title="LOS Credential Details"
        >
          <div className={styles["los-cred__modal-form"]}>
            {viewLoading || viewModal.loading ? (
              <p>Loading...</p>
            ) : (
              <>
                <Input label="Key Name" value={viewModal.name || ""} disabled />
                <Input
                  label="Client ID"
                  value={viewModal.clientRaw || ""}
                  showCopy
                  copyText={viewModal.clientRaw || ""}
                  onCopySuccess={() => showNotification("Copied to clipboard")}
                />
                <Input
                  label="Expires At"
                  value={formatDate(viewModal.expiresAt)}
                  disabled
                />

                <div>
                  <span className={styles["los-cred__field-label"]}>
                    Products
                  </span>
                  <div className={styles["los-cred__product-chips"]}>
                    {viewModal.products?.length ? (
                      viewModal.products.map((p) => (
                        <span
                          key={p.apiMasterId || p.id || p.name}
                          className={styles["los-cred__product-chip"]}
                        >
                          {p.name}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: "#94a3b8" }}>--</span>
                    )}
                  </div>
                </div>

                <div>
                  <span className={styles["los-cred__field-label"]}>
                    Allowed IP Addresses
                  </span>
                  {viewModal.ipList?.length ? (
                    <div className={styles["los-cred__ip-list"]}>
                      {viewModal.ipList.map((ip) => (
                        <span key={ip} className={styles["los-cred__ip-chip"]}>
                          {ip}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "#94a3b8", fontSize: "14px" }}>
                      No IP restrictions applied.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* EDIT MODAL */}
      {editModal && (
        <Modal onClose={() => setEditModal(null)} title="Edit LOS Credential">
          <div className={styles["los-cred__modal-form"]}>
            <Input
              type="select"
              label="Status"
              value={editModal.status}
              onChange={(e) =>
                setEditModal((prev) => ({ ...prev, status: e.target.value }))
              }
              options={[
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
            />

            <Input
              type="date"
              label="Expiry Date"
              value={editModal.expiry}
              onChange={(e) =>
                setEditModal((prev) => ({ ...prev, expiry: e.target.value }))
              }
            />

            <div>
              <span className={styles["los-cred__field-label"]}>
                IP Whitelist
              </span>
              <div className={styles["los-cred__ip-list"]}>
                {editModal.ipList.map((ip) => (
                  <div key={ip} className={styles["los-cred__ip-chip"]}>
                    {ip}
                    <div
                      className={styles["los-cred__ip-remove"]}
                      onClick={() =>
                        setEditModal((prev) => ({
                          ...prev,
                          ipList: prev.ipList.filter((i) => i !== ip),
                        }))
                      }
                    >
                      <X size={12} strokeWidth={3} />
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles["los-cred__ip-input-row"]}>
                <Input
                  placeholder="e.g. 203.0.113.10"
                  value={editIpInput}
                  onChange={(e) => setEditIpInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEditIp();
                    }
                  }}
                />
                <Button text="Add IP" onClick={addEditIp} />
              </div>
            </div>

            <div className={styles["los-cred__modal-actions"]}>
              <Button
                text={editSaving ? "Saving..." : "Save Changes"}
                disabled={editSaving}
                onClick={saveEdit}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default LosCredentialsTable;
